import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { 
  Button, 
  Modal, 
  ModalBody, 
  ModalFooter, 
  ModalHeader, 
  FormGroup, 
  Label, 
  Input,
  Badge,
  Alert,
  Spinner
} from "reactstrap";
import { toast } from "react-toastify";
import DOMPurify from "dompurify";
import { fetchOrdersRequest } from "../../store/orderManagement/actions";
import { FiEdit, FiX, FiCheck, FiClock, FiAlertCircle, FiUserX, FiShield, FiDollarSign } from "react-icons/fi";

const StatusModal = ({
  isOpen,
  toggle,
  status,
  orderData,
  updateOrderStatus,
  orderId,
  isServiceDeleted = false,
}) => {
  const [newStatus, setNewStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();

  const statusOptions = [
    { value: "pending", label: "قيد الانتظار", icon: <FiClock className="me-2" />, color: "warning" },
    { value: "approved", label: "موافق عليه", icon: <FiCheck className="me-2" />, color: "success" },
    { value: "progress", label: "قيد التنفيذ", icon: <FiAlertCircle className="me-2" />, color: "info" },
    { value: "completed", label: "مكتمل", icon: <FiCheck className="me-2" />, color: "primary" },
    { value: "cancelled_by_admin", label: "ألغي بواسطة الإدارة", icon: <FiShield className="me-2" />, color: "dark" },
    { value: "provider_delay", label: "تسجيل تأخير مقدم الخدمة", icon: <FiAlertCircle className="me-2" />, color: "warning" },
    // { value: "cancelled_by_provider", label: "ألغي بواسطة مقدم الخدمة", icon: <FiUserX className="me-2" />, color: "secondary" },
  ];

  useEffect(() => {
    if (isOpen) {
      setNewStatus("");
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleStatusChange = (event) => {
    const sanitizedValue = DOMPurify.sanitize(event.target.value);
    setNewStatus(sanitizedValue);
  };

  const getBookingExecuteDate = (bookingExecuteDate) => {
    if (!bookingExecuteDate) return null;
    if (bookingExecuteDate?.seconds) return new Date(bookingExecuteDate.seconds * 1000);

    const parsed = new Date(bookingExecuteDate);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const bookingStatus = String(orderData?.booking_status || status || "").toLowerCase();
  const paymentStatus = String(orderData?.paymentStatus || "").toLowerCase();
  const executeDate = getBookingExecuteDate(orderData?.booking_execute_date);
  const isDelayLocked = Boolean(orderData?.delay_locked);
  const isExecuteDatePassed = executeDate ? executeDate.getTime() < Date.now() : false;
  const canMarkDelay =
    isDelayLocked ||
    ((bookingStatus === "pending" || bookingStatus === "approved") &&
      paymentStatus === "paid" &&
      isExecuteDatePassed);

  const delayGuardReason = isDelayLocked
    ? null
    : !executeDate
    ? "لا يوجد booking_execute_date لهذا الطلب"
    : !(bookingStatus === "pending" || bookingStatus === "approved")
      ? "الحالة الحالية ليست Pending أو Approved"
      : paymentStatus !== "paid"
        ? "حالة الدفع ليست Paid"
        : !isExecuteDatePassed
          ? "موعد التنفيذ لم يمر بعد"
          : null;

  const handleConfirmStatusUpdate = async () => {
    if (!newStatus) {
      toast.info("الرجاء اختيار حالة جديدة");
      return;
    }

    setIsSubmitting(true);
    try {
      await updateOrderStatus(orderId, newStatus);
      if (newStatus !== "provider_delay") {
        toast.success("تم تحديث حالة الطلب بنجاح");
      }
      dispatch(fetchOrdersRequest());
      toggle();
    } catch (error) {
      toast.error("حدث خطأ أثناء تحديث حالة الطلب");
      console.error("Error updating order status:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCurrentStatus = () => {
    const current = statusOptions.find(opt => opt.value === status);
    return current || { label: status, color: "secondary" };
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={!isSubmitting ? toggle : undefined}
      centered
      backdrop="static"
      className="modal-lg"
    >
      <ModalHeader toggle={!isSubmitting ? toggle : undefined} className="border-0">
        <div className="d-flex align-items-center">
          <FiEdit className="me-2" size={20} />
          <h5 className="mb-0">تغيير حالة الطلب</h5>
        </div>
        {orderId && (
          <small className="text-muted">رقم الطلب: {orderId.slice(0, 8)}</small>
        )}
      </ModalHeader>
      
      <ModalBody>
        {isServiceDeleted && (
          <Alert color="warning" className="mb-3 d-flex align-items-center">
            <FiAlertCircle className="me-2" size={20} />
            <div>
              <strong>⚠️ الخدمة مؤرشفة</strong>
              <p className="mb-0 mt-1 small">
                تم أرشفة الخدمة المرتبطة بهذا الطلب. لا يمكن تغيير حالة الطلب إلا بعد استعادة الخدمة من قبل المسؤول.
              </p>
            </div>
          </Alert>
        )}

        <FormGroup>
          <Label for="currentStatus">الحالة الحالية</Label>
          <div className="d-flex align-items-center mb-3 p-2 bg-light rounded">
            <Badge color={getCurrentStatus().color} className="me-2">
              {getCurrentStatus().label}
            </Badge>
          </div>
        </FormGroup>

        {!isServiceDeleted && (
          <FormGroup>
            <Label for="statusSelect">اختر الحالة الجديدة</Label>
            <Input
              type="select"
              name="status"
              id="statusSelect"
              value={newStatus}
              onChange={handleStatusChange}
              disabled={isSubmitting}
              className="form-select-lg"
            >
              <option value="">اختر الحالة الجديدة...</option>
              {statusOptions.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={option.value === "provider_delay" && !canMarkDelay}
                >
                  {option.icon}
                  {option.label}
                </option>
              ))}
            </Input>
            <small className="text-muted d-block mt-2">
              تسجيل التأخير متاح إذا كان الطلب مؤهلاً للتأخير أو إذا كان التأخير مثبتاً مسبقاً على الطلب.
            </small>
            {isDelayLocked && (
              <Alert color="danger" className="mt-2 mb-0 py-2">
                هذا الطلب يحمل علامة تأخير مثبتة مسبقاً، ويمكن تسجيل المخالفة حتى بعد تغيير الحالة.
              </Alert>
            )}
            {!canMarkDelay && (
              <Alert color="light" className="mt-2 mb-0 py-2">
                <strong>سبب عدم توفر خيار التأخير:</strong> {delayGuardReason}
              </Alert>
            )}
          </FormGroup>
        )}

        {newStatus && !isServiceDeleted && (
          <Alert color="info" className="mt-3">
            <div className="d-flex align-items-center">
              <FiAlertCircle className="me-2" />
              <span>
                {newStatus === "provider_delay" ? (
                  <>
                    سيتم تسجيل مخالفة تأخير على مقدم الخدمة لهذا الطلب. أول مخالفة تكون إنذار فقط، وبعدها تُسجل غرامة 30 ر.س على كل تأخير جديد.
                  </>
                ) : (
                  <>
                    سيتم تغيير حالة الطلب من <strong>{getCurrentStatus().label}</strong> إلى{' '}
                    <strong>{statusOptions.find(opt => opt.value === newStatus)?.label}</strong>
                  </>
                )}
              </span>
            </div>
          </Alert>
        )}
      </ModalBody>

      <ModalFooter className="border-0">
        <Button
          color="primary"
          onClick={handleConfirmStatusUpdate}
          disabled={!newStatus || isSubmitting || isServiceDeleted}
          className="d-flex align-items-center"
        >
          {isSubmitting ? (
            <>
              <Spinner size="sm" className="me-2" />
              جاري التحديث...
            </>
          ) : (
            <>
              <FiCheck className="me-2" />
              تأكيد التغيير
            </>
          )}
        </Button>
        <Button 
          color="secondary" 
          onClick={toggle} 
          disabled={isSubmitting}
          className="d-flex align-items-center"
        >
          <FiX className="me-2" />
          إلغاء
        </Button>
      </ModalFooter>
    </Modal>
  );
};

StatusModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  status: PropTypes.string.isRequired,
  orderData: PropTypes.object,
  updateOrderStatus: PropTypes.func.isRequired,
  orderId: PropTypes.string,
  isServiceDeleted: PropTypes.bool,
};




export default StatusModal;